class BundleDrawer extends HTMLElement {
  constructor() {
    super();

    window.productVariantsInventory = window.productVariantsInventory || {};
    this.addToBundleHandle = this.addToBundleHandle.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.updateVariantPrice = this.updateVariantPrice.bind(this);
    this.clearProductItem = this.clearProductItem.bind(this);
    this.addToCartHandle = this.addToCartHandle.bind(this);
    this.editBundleItemHandle = this.editBundleItemHandle.bind(this);
    this.updateBundleHandle = this.updateBundleHandle.bind(this);

    this.initDrawer();
    this.loadProductsFromLocalStorage();
  }

  /**
   * Init drawer component
   */
  initDrawer() {
    this.drawer = this.querySelector('[js-bundle-drawer="bundle-drawer"]');
    this.addToBundle = document.querySelectorAll('[js-bundle-drawer="add-to-bundle"]');
    this.overlay = this.drawer.querySelector('[js-bundle-drawer="overlay"]');
    this.closeButton = this.drawer.querySelector('[js-bundle-drawer="close"]');
    this.addToCart = this.drawer.querySelector('[js-bundle-drawer="add-to-cart"]');
    this.bundleProduct = this.drawer.querySelector('[js-bundle-drawer="product-item"]');
    this.updateBundle = this.drawer.querySelector('[js-bundle-drawer="update-cart"]');
    this.basketPrice = document.querySelector('bundle-basket-bar');
    this.showUpdateContent = false;
    this.collectionHandle = document
      .querySelector('[js-bundle-tracker="container"]')
      .getAttribute('data-bundle-handle');

    this.addToBundle.forEach((button) => {
      button.addEventListener('click', this.addToBundleHandle);
    });

    this.closeButton.addEventListener('click', this.close);
    document.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    this.overlay.addEventListener('click', this.close);
    this.addToCart.addEventListener('click', this.addToCartHandle);

    document.addEventListener('click', (event) => {
      if (event.target.closest('[js-bundle-tracker="edit"]')) {
        this.editBundleItemHandle(event);
      }
      if (event.target.closest('[js-bundle-tracker="delete"]')) {
        this.deleteBundleItem(event);
      }
    });

    this.updateBundle.addEventListener('click', this.updateBundleHandle);
  }

  /**
   * Bundle handler to get product data.
   * @param event
   * @returns {Promise}
   */
  async addToBundleHandle(event) {
    const productHandle = event.target.dataset.productHandle;
    const productFirstVariantPrice = event.target.dataset.productFirstVariantPrice;
    const productDefaultVariant = event.target.dataset.productDefaultVariant;
    this.showUpdateContent = false;
    this.clearProductItem();

    try {
      const response = await fetch(`/products/${productHandle}.js`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      this.product = await response.json();
      this.buildProductItem(this.product, productFirstVariantPrice, productDefaultVariant);
      this.open();
    } catch (err) {
      throw new Error(err);
    }
  }

  /**
   * Open bundle-drawer.
   */
  open() {
    this.updateDrawerContent();

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.variantSelectHandle();
    });
    document.body.classList.add('overflow-hidden');

    const quantityInput = this.drawer.querySelector('[js-bundle-drawer="quantity"]');
    if (!quantityInput) {
      return;
    }

    quantityInput.addEventListener('input', (event) => {
      const errorMessage = this.drawer.querySelector('[js-bundle-drawer="quantity-error"]');
      if (parseInt(event.target.value, 10) > parseInt(quantityInput.getAttribute('max'), 10)) {
        errorMessage.classList.remove('hidden');
        if (this.updateBundle) {
          this.updateBundle.setAttribute('disabled', 'disabled');
        }

        this.addToCart.setAttribute('disabled', 'disabled');
        return;
      }

      if (this.updateBundle) {
        this.updateBundle.removeAttribute('disabled');
      }
      this.addToCart.removeAttribute('disabled');
      errorMessage.classList.add('hidden');
    });
  }

  /**
   * Close bundle drawer.
   */
  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  /**
   * Update drawer CTA and title.
   */
  updateDrawerContent() {
    this.addTitle = this.drawer.querySelector('[js-bundle-drawer="add-title"]');
    this.updateTitle = this.drawer.querySelector('[js-bundle-drawer="update-title"]');

    const toggleClass = (element, className, condition) => {
      element.classList.toggle(className, condition);
    };
    toggleClass(this.addTitle, 'hidden', this.showUpdateContent);
    toggleClass(this.addToCart, 'hidden', this.showUpdateContent);
    toggleClass(this.updateTitle, 'hidden', !this.showUpdateContent);
    toggleClass(this.updateBundle, 'hidden', !this.showUpdateContent);
  }

  /**
   * Build product item template.
   * @param {Object} product - Product object.
   * @param {String} productFirstVariantPrice - Product first variant price
   * @param {String} productDefaultVariant - Product has only default variant.
   * @param {Object} editData - Dta object for editing product (optional).
   */
  buildProductItem(product, productFirstVariantPrice, productDefaultVariant, editData = null) {
    let variantSelectHTML = '';
    const selectedVariantId = editData ? editData.variant : null;
    const selectedQuantity = editData ? editData.quantity : 1;
    const selectedVariantPrice = editData ? `${editData.price}` : productFirstVariantPrice;

    variantSelectHTML = this.buildVariantSelectHTML(
      product,
      productDefaultVariant,
      selectedVariantId,
      productFirstVariantPrice
    );

    const productHTML = `
      <div
        class="bundle-drawer__product"
        js-bundle-drawer="product"
      >
        <div class="bundle-drawer__product-top">
          <div class="bundle-drawer__product-image">
            <img
              src="${product.featured_image ? product.featured_image : product.image.src}"
              alt="${product.title}"
              js-bundle-drawer="image"
            >
          </div>
          <div class="bundle-drawer__product-content">
            <p
              class="bundle-drawer__product-title"
              js-bundle-drawer="title"
            >
              ${product.title}
            </p>
            
            <p
              class="bundle-drawer__product-price"
              js-bundle-drawer="variant-price"
            >
              ${selectedVariantPrice}
            </p>
          </div>
        </div>
        
        <div class="bundle-drawer__product-action">
          ${variantSelectHTML}
          
          <span class="bundle-drawer__error hidden">
            ${window.bundleDrawerStrings.quantity}
          </span>
          
          <label for="quantity">
            ${window.bundleDrawerStrings.quantity}
          </label>

          <span class="bundle-drawer__product-quantity-error hidden" js-bundle-drawer="quantity-error">
            ${window.bundleDrawerStrings.quantityError}
          </span>

          <input
            class="bundle-drawer__product-quantity bundle-form-element"
            js-bundle-drawer="quantity"
            type="number"
            id="quantity"
            min="1"
            max="99"
            value="${selectedQuantity}"
          >
        </div>
      </div>
    `;

    this.bundleProduct.setAttribute('data-product-handle', product.handle);
    this.bundleProduct.setAttribute('data-product-id', product.id);
    this.bundleProduct.insertAdjacentHTML('afterbegin', productHTML);
  }

  /**
   * Build variant selector/
   * @param {Object} product - Product object.
   * @param {String} productDefaultVariant - Product has only default variant.
   * @param {String} selectedVariantId - First available variant id.
   * @param {String} productFirstVariantPrice - First available variant price
   * @returns {string}
   */
  buildVariantSelectHTML(product, productDefaultVariant, selectedVariantId, productFirstVariantPrice) {
    const isHidden = productDefaultVariant === 'true';
    return `
      <label for="variant-select" class="${isHidden ? 'hidden' : ''}">
        ${window.bundleDrawerStrings.selectDefaultOption}
      </label>

      <select
        id="variant-select"
        class="bundle-drawer__product-variants bundle-form-element ${isHidden ? 'hidden' : ''}"
        js-bundle-drawer="variant-select"
      >
        <option variant-price="${productFirstVariantPrice}" disabled>
          ${window.bundleDrawerStrings.selectDefaultOption}
        </option>

        ${product.variants
          .map(
            (variant) => `
          <option
            ${!variant.available ? 'disabled' : ''}
            value="${variant.id}"
            variant-price="${window.bundleDrawerStrings.moneyFormat}${(variant.price / 100).toFixed(2)}"
            ${String(variant.id) === selectedVariantId ? 'selected="selected"' : ''}
          >
            ${variant.title}
          </option>
        `
          )
          .join('')}
      </select>
    `;
  }

  /**
   * Variant select handle.
   */
  variantSelectHandle() {
    this.variantSelect = this.drawer.querySelector('[js-bundle-drawer="variant-select"]');
    this.variantPrice = this.drawer.querySelector('[js-bundle-drawer="variant-price"]');
    this.quantityInput = this.drawer.querySelector('[js-bundle-drawer="quantity"]');

    if (!this.variantSelect) {
      return;
    }

    this.updateQuantityMax(this.variantSelect.value);

    this.variantSelect.addEventListener('change', (event) => {
      this.updateQuantityMax(event.target.value);
      this.updateVariantPrice(event);
    });
  }

  /**
   * Update quantity input max attribute.
   * @param {Number} variantId - Variant ID.
   */
  updateQuantityMax(variantId) {
    this.quantityInput.setAttribute(
      'max',
      eval('window.productInventory_' + this.bundleProduct.dataset.productId)[variantId] !== undefined
        ? eval('window.productInventory_' + this.bundleProduct.dataset.productId)[variantId]
        : 99
    );
  }

  /**
   * Price change handle.
   * @param event
   */
  updateVariantPrice(event) {
    const selectedOption = event.target.selectedOptions[0];
    selectedOption.setAttribute('selected', 'selected');
    this.variantPrice.textContent = selectedOption.getAttribute('variant-price');
  }

  /**
   * Remove product item.
   */
  clearProductItem() {
    const productElement = this.drawer.querySelector('[js-bundle-drawer="product"]');

    if (productElement) {
      productElement.remove();
    }
  }

  getSelectedVariantId() {
    const variantSelected = this.bundleProduct.querySelector('[js-bundle-drawer="variant-select"]');
    return variantSelected ? variantSelected.selectedOptions[0].value : null;
  }

  /**
   * Add bundle handle.
   */
  addToCartHandle() {
    const selectedVariantId = this.getSelectedVariantId();
    const selectedVariant = this.product.variants.find((variant) => variant.id === selectedVariantId);
    const price = selectedVariant
      ? selectedVariant.price
      : this.bundleProduct
          .querySelector('[js-bundle-drawer="variant-select"]')
          .selectedOptions[0].getAttribute('variant-price');
    let products = JSON.parse(localStorage.getItem(`bundleProducts_${this.collectionHandle}`)) || [];
    const existingProductIndex = products.findIndex((prod) => prod.selectedVariant === selectedVariantId);

    if (existingProductIndex !== -1) {
      let existingProduct = products[existingProductIndex];
      const newQuantity =
        parseInt(existingProduct.quantity) +
        parseInt(this.bundleProduct.querySelector('[js-bundle-drawer="quantity"]').value);
      existingProduct.quantity = newQuantity;
      products[existingProductIndex] = existingProduct;
      document.querySelector(`[js-bundle-tracker="quantity"][data-variant-id="${selectedVariantId}"]`).innerHTML =
        newQuantity;
    } else {
      const product = {
        id: this.product.id,
        handle: this.product.handle,
        image: { src: this.bundleProduct.querySelector('[js-bundle-drawer="image"]').src },
        title: this.bundleProduct.querySelector('[js-bundle-drawer="title"]').textContent,
        quantity: this.bundleProduct.querySelector('[js-bundle-drawer="quantity"]').value,
        selectedVariant: selectedVariantId,
        price: price,
        variants: this.product.variants
      };
      products.push(product);
      this.addProductToBundleTracker(product);
    }

    localStorage.setItem(`bundleProducts_${this.collectionHandle}`, JSON.stringify(products));
    localStorage.setItem(
      `bundleWrapperHTML_${this.collectionHandle}`,
      document.querySelector('[js-bundle-tracker="wrapper"]').innerHTML
    );
    this.basketPrice.updateTotalPriceDisplay();
    this.close();
  }

  /**
   * Handle bundle update.
   */
  updateBundleHandle() {
    const selectedVariantId = this.getSelectedVariantId();
    const productHandle = this.bundleProduct.dataset.productHandle;
    const products = JSON.parse(localStorage.getItem(`bundleProducts_${this.collectionHandle}`)) || [];
    const existingProduct = products.find((prod) => prod.handle === productHandle);

    if (!existingProduct) {
      return;
    }

    const selectedVariant = existingProduct.variants.find((variant) => variant.id === selectedVariantId);
    const price = selectedVariant
      ? selectedVariant.price
      : this.bundleProduct
          .querySelector('[js-bundle-drawer="variant-select"]')
          .selectedOptions[0].getAttribute('variant-price');

    const updatedProduct = {
      handle: existingProduct.handle,
      image: { src: this.bundleProduct.querySelector('[js-bundle-drawer="image"]').src },
      title: this.bundleProduct.querySelector('[js-bundle-drawer="title"]').textContent,
      quantity: this.bundleProduct.querySelector('[js-bundle-drawer="quantity"]').value,
      selectedVariant: selectedVariantId,
      price: price,
      variants: existingProduct.variants
    };

    this.updateProductInLocalStorage(updatedProduct);
    this.updateProductInBundleTracker(updatedProduct);
    this.basketPrice.updateTotalPriceDisplay();
    this.saveWrapperHTMLToLocalStorage();
    this.close();
  }

  /**
   * Edit current bundle item.
   * @param event
   */
  editBundleItemHandle(event) {
    const itemProductElement = event.target.closest('[js-bundle-tracker="item-product"]');
    const productPrice = itemProductElement.dataset.productPrice;
    this.showUpdateContent = true;
    const productEditData = {
      variant: itemProductElement.dataset.productVariant,
      quantity: itemProductElement.querySelector('[js-bundle-tracker="quantity"]').innerText,
      price: productPrice
    };
    const products = JSON.parse(localStorage.getItem(`bundleProducts_${this.collectionHandle}`)) || [];
    const product = products.find((prod) => prod.handle === itemProductElement.dataset.productHandle);

    this.clearProductItem();
    this.buildProductItem(product, productPrice, productEditData);
    this.open();
  }

  /**
   * Update product in the bundle tracker.
   * @param {Object} product - Product object to be updated.
   */
  updateProductInBundleTracker(product) {
    const itemProductElement = document.querySelector(
      `[js-bundle-tracker="item-product"][data-product-handle="${product.handle}"]`
    );

    if (!itemProductElement) {
      this.addProductToBundleTracker(product);
    }

    itemProductElement.dataset.productPrice = product.price;
    itemProductElement.dataset.productVariant = product.selectedVariant;
    itemProductElement.querySelector('[js-bundle-tracker="quantity"]').innerText = product.quantity;
  }

  /**
   * Update product in local storage.
   * @param {Object} product - Product object to be updated.
   */
  updateProductInLocalStorage(product) {
    let products = JSON.parse(localStorage.getItem(`bundleProducts_${this.collectionHandle}`)) || [];
    const productIndex = products.findIndex((prod) => prod.handle === product.handle);

    products =
      productIndex !== -1
        ? products.map((prod, index) => (index === productIndex ? product : prod))
        : [...products, product];

    localStorage.setItem(`bundleProducts_${this.collectionHandle}`, JSON.stringify(products));
  }

  /**
   * Add product to the bundle tracker section.
   * @param product
   */
  addProductToBundleTracker(product) {
    const placeholders = document.querySelectorAll('[js-bundle-tracker="item-placeholder"]');
    const placeholder = document.querySelector('[js-bundle-tracker="item-placeholder"]');
    const productHTML = this.generateProductHTML(product);

    if (placeholders.length > 0 && placeholder === placeholders[placeholders.length - 1]) {
      const trackerCarousel = document.querySelector('[js-bundle-tracker="container"]').swiper;
      const placeholderHTML = `
        <div
          class="bundle-tracker__item-placeholder swiper-slide"
          js-bundle-tracker="item-placeholder"
        >
          <span
            class="bundle-tracker__item-icon"
            js-bundle-tracker="item-icon"
          >
            ${window.bundleDrawerStrings.placeholderIcon}
          </span>
        </div>
      `;

      trackerCarousel.appendSlide(placeholderHTML);
    }

    if (placeholder) {
      this.updatePlaceholder(placeholder, productHTML);
    }

    this.addNewProduct(placeholder, productHTML);
    this.saveWrapperHTMLToLocalStorage();
  }

  /**
   * Generate product HTML
   * @param {Object} product - Product object
   * @returns {string} - HTML string for the product
   */
  generateProductHTML(product) {
    return `
      <div
        class="bundle-drawer__item"
        js-bundle-tracker="item-product"
        data-product-handle="${product.handle}"
        data-product-price="${product.price}"
        data-product-variant="${product.selectedVariant}"
        data-product-id="${product.id}"
      >      
        <div class="bundle-drawer__item-block">
          <button
            type="button"
            class="bundle-drawer__item-delete"
            js-bundle-tracker="delete"
          >
            <span class="bundle-drawer__item-icon-block">
              ${window.bundleDrawerStrings.crossIcon}
            </span>
          </button>
        
          <img
            class="bundle-drawer__item-image"
            src="${product.image && product.image.src ? product.image.src : ''}"
            alt="${product.title}"
          >
          
          <span
            class="bundle-drawer__item-quantity icon-bundle"
            js-bundle-tracker="quantity"
            data-variant-id="${product.selectedVariant}"
          >
            ${product.quantity}
          </span>
          
          <button
            type="button"
            data-product-handle="${product.handle}"
            class="bundle-drawer__item-edit icon-bundle"
            js-bundle-tracker="edit"
          >
            <span class="bundle-drawer__item-icon-block">
              ${window.bundleDrawerStrings.editIcon}
            </span>
          </button>
        </div>

        <p
          class="bundle-drawer__item-title"
          js-bundle-drawer="bundle-title"
        >
          ${product.title}
        </p>
      </div>
    `;
  }

  /**
   * Add new product to the container
   * @param {HTMLElement} container - The container element to append the new product
   * @param {string} productHTML - The HTML content to insert into the new product
   */
  addNewProduct(container, productHTML) {
    container.insertAdjacentHTML('beforeend', productHTML);
  }

  /**
   * Update existing placeholder with product HTML
   * @param {HTMLElement} placeholder - The placeholder element to update
   * @param {string} productHTML - The HTML content to insert into the placeholder
   */
  updatePlaceholder(placeholder, productHTML) {
    placeholder.classList.remove('bundle-tracker__item-placeholder');
    placeholder.querySelector('[js-bundle-tracker="item-icon"]').classList.add('hidden');
    placeholder.setAttribute('js-bundle-tracker', 'item');
  }

  /**
   * Load products from localStorage and render them in the bundle tracker
   */
  loadProductsFromLocalStorage() {
    const products = JSON.parse(localStorage.getItem(`bundleProducts_${this.collectionHandle}`)) || [];
    if (products.length > 0) {
      this.loadWrapperHTMLFromLocalStorage();
    }
  }

  /**
   * Load product wrapper HTML object from local storage.
   */
  loadWrapperHTMLFromLocalStorage() {
    const wrapper = document.querySelector('[js-bundle-tracker="wrapper"]');
    const savedHTML = localStorage.getItem(`bundleWrapperHTML_${this.collectionHandle}`);

    if (savedHTML) {
      wrapper.innerHTML = savedHTML;
    }
  }

  /**
   * Delete bundle item from bundle tracker.
   */
  deleteBundleItem(event) {
    const itemProduct = event.target.closest('[js-bundle-tracker="item-product"]');
    if (!itemProduct) {
      return;
    }

    const parent = itemProduct.parentNode;
    if (!parent) {
      return;
    }

    const target = document.querySelector('.bundle-tracker__wrapper'),
        div = document.createElement('div');

    div.classList.add('bundle-tracker__item-placeholder', 'swiper-slide');
    div.setAttribute('js-bundle-tracker', 'item-placeholder');
    div.innerHTML = `
            <span  class="bundle-tracker__item-icon" js-bundle-tracker="item-icon">
              ${window.bundleDrawerStrings.placeholderIcon}
            </span>
          `;

    if (parent.querySelector('[js-bundle-tracker="item-icon"]')) {
      target.appendChild(div);
    }

    itemProduct.parentNode.remove();
    let products = JSON.parse(localStorage.getItem(`bundleProducts_${this.collectionHandle}`)) || [];
    products = products.filter((product) => product.selectedVariant !== itemProduct.dataset.productVariant);
    localStorage.setItem(`bundleProducts_${this.collectionHandle}`, JSON.stringify(products));
    this.basketPrice.updateTotalPriceDisplay();
    this.saveWrapperHTMLToLocalStorage();
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * Save wrapper with product item to local storage.
   */
  saveWrapperHTMLToLocalStorage() {
    localStorage.setItem(
      `bundleWrapperHTML_${this.collectionHandle}`,
      document.querySelector('[js-bundle-tracker="wrapper"]').innerHTML
    );
  }
}

customElements.define('bundle-drawer', BundleDrawer);
